import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import {
  fetchDocumentTemplates,
  uploadDocumentTemplate,
  fetchTemplateHistory,
  downloadCurrentTemplate,
  downloadTemplateVersion,
} from "Services/AdminServices/DocumentsStoreService";
import { TEMPLATE_ALLOWED_EXTENSIONS } from "Constants/Enums/HiringDocumentTemplateEnum";

const getExtension = (name = "") => {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
};

const extractError = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.details ||
  err?.message ||
  fallback;

// Company Documents template repository (per Mudassir Jul-15 handoff).
// Loads the nine template slots, handles upload/replace (backend auto-versions),
// current + version downloads, and drives the version-history modal.
export default function useCompanyTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  // documentType currently being uploaded/replaced (drives per-row loading)
  const [uploadingType, setUploadingType] = useState(null);

  const [history, setHistory] = useState({
    open: false,
    documentType: null,
    displayName: "",
    loading: false,
    versions: [],
  });

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchDocumentTemplates();
      setTemplates(list);
    } catch (err) {
      message.error(extractError(err, "Failed to load templates."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // First upload or replace. `file` is the raw File from AntD's beforeUpload.
  const handleUploadFile = async (template, file) => {
    const ext = getExtension(file.name);
    if (!TEMPLATE_ALLOWED_EXTENSIONS.includes(ext)) {
      message.error("Only PDF, DOC, and DOCX files are allowed.");
      return;
    }
    if (!file.size) {
      message.error("The selected file is empty.");
      return;
    }
    setUploadingType(template.documentType);
    try {
      await uploadDocumentTemplate(template.documentType, file);
      message.success(
        `${template.displayName} ${template.isUploaded ? "replaced" : "uploaded"} successfully.`
      );
      await loadTemplates();
    } catch (err) {
      message.error(extractError(err, "Upload failed. Please try again."));
    } finally {
      setUploadingType(null);
    }
  };

  const handleDownloadCurrent = async (template) => {
    try {
      await downloadCurrentTemplate(
        template.documentType,
        template.originalFileName || template.displayName
      );
    } catch (err) {
      message.error(extractError(err, "Download failed."));
    }
  };

  const openHistory = async (template) => {
    setHistory({
      open: true,
      documentType: template.documentType,
      displayName: template.displayName,
      loading: true,
      versions: [],
    });
    try {
      const versions = await fetchTemplateHistory(template.documentType);
      setHistory((prev) => ({ ...prev, loading: false, versions }));
    } catch (err) {
      message.error(extractError(err, "Failed to load version history."));
      setHistory((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeHistory = () =>
    setHistory({
      open: false,
      documentType: null,
      displayName: "",
      loading: false,
      versions: [],
    });

  const handleDownloadVersion = async (versionRow) => {
    try {
      await downloadTemplateVersion(
        versionRow.hiringDocumentTemplateId,
        versionRow.originalFileName || `${history.displayName}-v${versionRow.version}`
      );
    } catch (err) {
      message.error(extractError(err, "Download failed."));
    }
  };

  return {
    templates,
    loading,
    uploadingType,
    history,
    loadTemplates,
    handleUploadFile,
    handleDownloadCurrent,
    openHistory,
    closeHistory,
    handleDownloadVersion,
  };
}
